import { Link } from "@tanstack/react-router";
import {
  ArrowRight,
  Building2,
  Calendar,
  ClipboardList,
  FileText,
  MapPin,
  TrendingUp,
} from "lucide-react";
import { ProjectProgressBar } from "@/components/projects/ProjectProgressBar";
import { Skeleton } from "@/components/ui/skeleton";
import type { Milestone } from "@/hooks/use-milestones";
import { useAllProjectMilestones } from "@/hooks/use-milestones";
import { useProjects } from "@/hooks/use-projects";
import { usePunchList } from "@/hooks/use-punch-list";
import { useRfis } from "@/hooks/use-rfis";

function formatCurrency(value: number): string {
  if (value >= 1_000_000) return `$${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `$${(value / 1_000).toFixed(0)}K`;
  return `$${value.toLocaleString()}`;
}

interface KpiCardProps {
  icon: React.ComponentType<{ className?: string }>;
  title: string;
  value: string | number;
  isLoading: boolean;
  iconColor: string;
  iconBg: string;
}

function KpiCard({ icon: Icon, title, value, isLoading, iconColor, iconBg }: KpiCardProps) {
  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm font-medium text-gray-600">{title}</p>
          {isLoading ? (
            <Skeleton className="mt-2 h-8 w-20" />
          ) : (
            <p className="mt-1 text-3xl font-bold text-gray-900">{value}</p>
          )}
        </div>
        <div className={`flex h-12 w-12 items-center justify-center rounded-full ${iconBg}`}>
          <Icon className={`h-6 w-6 ${iconColor}`} />
        </div>
      </div>
    </div>
  );
}

const statusConfig: Record<string, { bg: string; text: string; label: string }> = {
  active: { bg: "bg-green-100", text: "text-green-700", label: "Active" },
  "pre-construction": { bg: "bg-gray-100", text: "text-gray-700", label: "Pre-Construction" },
  "on-hold": { bg: "bg-amber-100", text: "text-amber-700", label: "On Hold" },
  completed: { bg: "bg-blue-100", text: "text-blue-700", label: "Complete" },
  cancelled: { bg: "bg-gray-100", text: "text-gray-700", label: "Cancelled" },
};

function StatusBadge({ status }: { status: string }) {
  const config = statusConfig[status] ?? {
    bg: "bg-gray-100",
    text: "text-gray-700",
    label: status,
  };
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs font-medium px-2 py-0.5 rounded-full ${config.bg} ${config.text}`}
    >
      {config.label}
    </span>
  );
}

export function DashboardPage() {
  const { data: projects, isLoading: projectsLoading } = useProjects();
  const { data: rfis, isLoading: rfisLoading } = useRfis();
  const { data: punchList, isLoading: punchLoading } = usePunchList();
  const { data: allMilestones } = useAllProjectMilestones();

  const milestonesByProject = (allMilestones ?? []).reduce<Record<string, Milestone[]>>(
    (acc, m) => {
      const bucket = acc[m.projectId] ?? [];
      bucket.push(m);
      acc[m.projectId] = bucket;
      return acc;
    },
    {},
  );

  const allProjects = projects ?? [];
  const activeProjects = allProjects.filter((p) => p.status === "active").length;
  const openRfis = (rfis ?? []).filter((r) => r.status === "open" || r.status === "draft").length;
  const openPunch = (punchList ?? []).filter((p) => p.status === "open").length;
  const totalContractValue = allProjects.reduce(
    (sum, p) => sum + (Number(p.contractValue) || 0),
    0,
  );

  return (
    <div className="space-y-8">
      <h1 className="text-2xl font-bold text-[#334155]">Portfolio Dashboard</h1>

      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <KpiCard
          icon={TrendingUp}
          title="Active Projects"
          value={activeProjects}
          isLoading={projectsLoading}
          iconColor="text-blue-600"
          iconBg="bg-blue-50"
        />
        <KpiCard
          icon={FileText}
          title="Open RFIs"
          value={openRfis}
          isLoading={rfisLoading}
          iconColor="text-amber-600"
          iconBg="bg-amber-50"
        />
        <KpiCard
          icon={ClipboardList}
          title="Open Punch Items"
          value={openPunch}
          isLoading={punchLoading}
          iconColor="text-red-600"
          iconBg="bg-red-50"
        />
        <KpiCard
          icon={Building2}
          title="Total Contract Value"
          value={formatCurrency(totalContractValue)}
          isLoading={projectsLoading}
          iconColor="text-emerald-600"
          iconBg="bg-emerald-50"
        />
      </div>

      {/* Active Projects */}
      <div>
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-[#334155]">Projects</h2>
          <Link
            to="/projects"
            className="inline-flex items-center gap-1 text-sm font-medium text-[#334155] hover:text-[#415A77] transition-colors"
          >
            View all
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>

        {projectsLoading ? (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {[1, 2, 3].map((key) => (
              <div key={key} className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                <Skeleton className="h-5 w-3/4 mb-3" />
                <Skeleton className="h-4 w-1/2 mb-2" />
                <Skeleton className="h-4 w-2/3 mb-4" />
                <Skeleton className="h-2 w-full rounded-full" />
              </div>
            ))}
          </div>
        ) : allProjects.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-12 text-center">
            <Building2 className="h-12 w-12 text-gray-300 mx-auto mb-3" />
            <p className="text-sm text-gray-500">
              No projects yet. Create your first project to get started.
            </p>
          </div>
        ) : (
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {allProjects.map((project) => (
              <Link
                key={project.id}
                to="/projects/$projectId"
                params={{ projectId: project.id }}
                search={{ tab: "overview" }}
                className="block group"
              >
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5 hover:shadow-md transition-shadow cursor-pointer h-full">
                  <div className="flex items-start justify-between mb-3">
                    <h3 className="text-base font-semibold text-gray-900 leading-tight group-hover:text-[#334155] transition-colors">
                      {project.name}
                    </h3>
                    <StatusBadge status={project.status} />
                  </div>

                  <div className="space-y-1.5 mb-4">
                    {project.type && <p className="text-sm text-gray-600">{project.type}</p>}
                    {project.location && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <MapPin className="h-3.5 w-3.5 shrink-0" />
                        <span className="truncate">{project.location}</span>
                      </div>
                    )}
                    {(project.startDate ?? project.endDate) && (
                      <div className="flex items-center gap-1.5 text-sm text-gray-500">
                        <Calendar className="h-3.5 w-3.5 shrink-0" />
                        <span>
                          {project.startDate
                            ? new Date(project.startDate).toLocaleDateString("en-US", {
                                month: "short",
                                year: "numeric",
                              })
                            : ""}
                          {project.startDate && project.endDate ? " — " : ""}
                          {project.endDate
                            ? new Date(project.endDate).toLocaleDateString("en-US", {
                                month: "short",
                                year: "numeric",
                              })
                            : ""}
                        </span>
                      </div>
                    )}
                  </div>

                  <ProjectProgressBar milestones={milestonesByProject[project.id] ?? []} />

                  {project.contractValue && (
                    <div className="mt-3 pt-3 border-t border-gray-100">
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-500">Contract Value</span>
                        <span className="text-sm font-semibold text-gray-900">
                          {formatCurrency(Number(project.contractValue))}
                        </span>
                      </div>
                    </div>
                  )}
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
